import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaCheckDouble, FaStar } from 'react-icons/fa';
import { getMediaUrl } from '@/constants/media';
import { formatClockTime, formatDateTime } from '@/utils/formatTime';
import { BotBadge } from '@/components/BotBadge';
import './MessageBubble.scss';

interface MessageBubbleProps {
  content: string;
  timestamp: string;
  isOwn: boolean;
  senderName: string;
  senderAvatar?: string | null;
  senderUsername?: string;
  /** Server-driven bot flag (see docs/klyx_bots.md). Renders a
   *  "🤖 Bot" chip inline with the timestamp so viewers know a
   *  bot is DMing them, per the bot-disclosure rule. */
  senderIsBot?: boolean;
  read?: boolean;
  isSpotlighted?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  content,
  timestamp,
  isOwn,
  senderName,
  senderAvatar,
  senderUsername,
  senderIsBot = false,
  read = false,
  isSpotlighted = false,
}) => {
  const navigate = useNavigate();

  const formatTime = (isoString: string): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    // Today: just the clock. Older: calendar + clock (year drops for
    // this-year entries via formatDateTime's adaptive logic).
    const isToday = date.toDateString() === new Date().toDateString();
    return isToday ? formatClockTime(date) : formatDateTime(date);
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (senderUsername && !isOwn) {
      navigate(`/profile/${senderUsername}`);
    }
  };

  return (
    <div className={`message-bubble ${isOwn ? 'own' : 'other'} ${isSpotlighted ? 'spotlighted' : ''}`}>
      {!isOwn && (
        <div
          className="message-avatar clickable"
          onClick={handleAvatarClick}
          title={`View ${senderName}'s profile`}
        >
          {senderAvatar ? (
            <img src={getMediaUrl(senderAvatar) || undefined} alt={senderName} />
          ) : (
            <div className="avatar-placeholder">
              {senderName[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>
      )}
      <div className="message-content-wrapper">
        {isSpotlighted && (
          <div className="spotlight-badge">
            <FaStar />
            <span>Spotlighted</span>
          </div>
        )}
        <div className="message-bubble-content">
          <p className="message-text">{content}</p>
        </div>
        <div className="message-footer">
          <span className="message-timestamp">{formatTime(timestamp)}</span>
          {/* Bot disclosure — every bot-sent message shows the chip so
              viewers know a bot is DMing them (docs/klyx_bots.md). */}
          {!isOwn && <BotBadge isBot={senderIsBot} />}
          {isOwn && (
            <span className={`message-status ${read ? 'read' : 'sent'}`}>
              {read ? <FaCheckDouble /> : <FaCheck />}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
